/**
 * Texto con formato, escrito desde la consola y leído por un paciente.
 *
 * La institución ya no escribe Markdown a mano: escribe en un editor con
 * botones y lo que queda guardado es HTML. Eso obliga a ser explícito con qué
 * HTML se acepta, porque **lo que se guarda acá termina en la pantalla de una
 * persona enferma**: un `<script>` pegado desde Word, un `onerror` en una
 * imagen o un `href="javascript:"` en un enlace serían código ajeno corriendo
 * en la sesión de cada paciente del programa.
 *
 * Por eso la lista es blanca y no negra. Todo lo que no esté nombrado acá se
 * cae: no se intenta "limpiar" una etiqueta desconocida, se la descarta. Una
 * lista negra siempre se queda corta —hay una forma más de escribir
 * `javascript:` que la que uno pensó— y el costo de equivocarse no es un
 * artículo mal formateado.
 *
 * Vive en `shared` porque la sanitización tiene que correr en la API, que es la
 * única que no se puede saltear: el editor del navegador es comodidad, no
 * defensa.
 */
/** Lo que el editor sabe producir y el lector sabe dibujar. */
const ALLOWED_TAGS = new Set([
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'h2',
    'h3',
    'h4',
    'ul',
    'ol',
    'li',
    'blockquote',
    'a',
    'code',
    'pre',
    'hr',
    // Para color y tamaño de letra. Es el único que admite `style`, y con una
    // lista blanca de valores: `style` suelto es una vía conocida para tapar la
    // pantalla con una capa transparente encima de un botón.
    'span',
]);
/** Los únicos atributos que sobreviven, y solo donde tienen sentido. */
const ALLOWED_ATTRS = {
    a: new Set(['href', 'title']),
    span: new Set(['style']),
};
/**
 * Los tamaños de letra que el editor ofrece, en píxeles.
 *
 * Cerrado a propósito: con un número libre alguien pone 96 y le rompe la
 * lectura a todo el que abra ese contenido en un teléfono.
 */
export const RICHTEXT_SIZES = [12, 14, 16, 18, 20, 24, 28, 32];
/**
 * Solo `color` y `font-size`, y con valores que se puedan verificar.
 *
 * Un `style` libre alcanza para dibujar una capa invisible sobre un botón —o
 * para tapar el contenido con `position: fixed`—, así que no se limpia lo que
 * llega: se descarta todo lo que no sea exactamente una de estas dos cosas.
 */
function safeStyle(value) {
    const salida = [];
    for (const parte of value.split(';')) {
        const [propiedad, ...resto] = parte.split(':');
        if (!propiedad || resto.length === 0)
            continue;
        const nombre = propiedad.trim().toLowerCase();
        const valor = resto.join(':').trim().toLowerCase();
        if (nombre === 'color' && /^#[0-9a-f]{3,8}$|^rgb\([\d\s,.%]+\)$/.test(valor)) {
            salida.push(`color: ${valor}`);
            continue;
        }
        if (nombre === 'font-size') {
            // En píxeles y dentro de la lista: con un número libre alguien pone 96 y
            // le rompe la lectura a todo el que abra ese contenido en un teléfono.
            const px = /^(\d+)px$/.exec(valor);
            if (px && RICHTEXT_SIZES.includes(Number(px[1]))) {
                salida.push(`font-size: ${valor}`);
            }
        }
    }
    return salida.length > 0 ? salida.join('; ') : null;
}
/** Etiquetas que se cierran solas y por lo tanto nunca llevan contenido. */
const VOID_TAGS = new Set(['br', 'hr']);
/**
 * Un `href` que no lleve a ejecutar algo.
 *
 * `javascript:`, `data:` y `vbscript:` son las tres formas clásicas de meter
 * código en un enlace. Se admite además el enlace relativo y el ancla, que son
 * los que escribe alguien pegando una URL de su propio sitio.
 */
function safeHref(value) {
    const trimmed = value.trim();
    // Se saca todo lo que no se ve antes de mirar el esquema: `java\nscript:` es
    // el truco de siempre para pasar una comparación literal.
    const bare = trimmed.replace(/[ - ]/g, '').toLowerCase();
    if (bare.startsWith('javascript:') || bare.startsWith('data:') || bare.startsWith('vbscript:')) {
        return null;
    }
    if (/^https?:\/\//i.test(trimmed))
        return trimmed;
    if (/^mailto:/i.test(trimmed) || /^tel:/i.test(trimmed))
        return trimmed;
    if (trimmed.startsWith('/') || trimmed.startsWith('#'))
        return trimmed;
    return null;
}
function escapeText(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/**
 * Deja el HTML en la forma mínima que el lector sabe dibujar.
 *
 * Se recorre con una expresión regular y no con un parser de DOM a propósito:
 * esto corre en Node, donde no hay DOM, y el resultado tiene que ser el mismo
 * que produciría el navegador. Como la lista es blanca, un HTML raro no se
 * "interpreta mal": se queda sin etiquetas y sobrevive el texto.
 */
export function sanitizeRichText(input) {
    if (!input)
        return '';
    // Bloques enteros que nunca deben pasar, con su contenido: un `<script>` sin
    // etiquetas seguiría dejando su código como texto suelto en el artículo.
    let html = input
        .replace(/<script\b[\s\S]*?<\/script\s*>/gi, '')
        .replace(/<style\b[\s\S]*?<\/style\s*>/gi, '')
        .replace(/<!--[\s\S]*?-->/g, '');
    const abiertas = [];
    let salida = '';
    let resto = html;
    const TAG = /<\/?\s*([a-zA-Z][a-zA-Z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)\/?>/;
    for (;;) {
        const match = TAG.exec(resto);
        if (!match) {
            salida += escapeText(resto);
            break;
        }
        salida += escapeText(resto.slice(0, match.index));
        const [entera, nombreCrudo, atributosCrudos] = match;
        const nombre = nombreCrudo.toLowerCase();
        const cierre = entera.startsWith('</');
        resto = resto.slice(match.index + entera.length);
        if (!ALLOWED_TAGS.has(nombre))
            continue;
        if (cierre) {
            // Solo se cierra lo que se abrió: un `</p>` suelto no tiene qué cerrar y
            // dejarlo pasar desbalancearía el árbol del lector.
            const posicion = abiertas.lastIndexOf(nombre);
            if (posicion === -1)
                continue;
            abiertas.splice(posicion, 1);
            salida += `</${nombre}>`;
            continue;
        }
        const atributos = limpiarAtributos(nombre, atributosCrudos ?? '');
        if (VOID_TAGS.has(nombre)) {
            salida += `<${nombre}${atributos}>`;
            continue;
        }
        abiertas.push(nombre);
        salida += `<${nombre}${atributos}>`;
    }
    // Lo que quedó abierto se cierra en orden inverso: un artículo cortado a la
    // mitad no puede arrastrar el resto de la pantalla adentro de un `<em>`.
    for (const nombre of [...abiertas].reverse())
        salida += `</${nombre}>`;
    return salida.trim();
}
function limpiarAtributos(tag, crudos) {
    const permitidos = ALLOWED_ATTRS[tag];
    if (!permitidos)
        return '';
    let salida = '';
    const ATTR = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+))/g;
    let match;
    while ((match = ATTR.exec(crudos)) !== null) {
        const nombre = match[1].toLowerCase();
        if (!permitidos.has(nombre))
            continue;
        const valor = match[3] ?? match[4] ?? match[5] ?? '';
        if (nombre === 'href') {
            const href = safeHref(valor);
            if (!href)
                continue;
            salida += ` href="${escapeText(href)}"`;
            continue;
        }
        if (nombre === 'style') {
            const style = safeStyle(valor);
            if (!style)
                continue;
            salida += ` style="${escapeText(style)}"`;
            continue;
        }
        salida += ` ${nombre}="${escapeText(valor)}"`;
    }
    return salida;
}
/**
 * Si un texto guardado es HTML del editor o Markdown de antes.
 *
 * Los contenidos que ya estaban cargados se escribieron en Markdown, y siguen
 * teniendo que leerse: migrarlos en silencio sería reescribir textos que
 * alguien redactó. El lector elige con qué dibujarlos.
 */
export function looksLikeHtml(source) {
    return /<(p|h2|h3|h4|ul|ol|li|strong|em|blockquote|br|a|u|s|pre|code|hr)\b/i.test(source);
}
/**
 * Markdown → HTML, para abrir en el editor lo que se escribió antes.
 *
 * Los contenidos cargados antes del editor están en Markdown. El editor los
 * metía tal cual en el área editable, así que se veían los `##` y los `**` como
 * texto: quien abría un artículo viejo encontraba los símbolos a la vista, y la
 * vista previa —que sí los interpreta— mostraba otra cosa.
 *
 * Convierte el mismo subconjunto que dibuja el lector de Markdown, y nada más:
 * lo que no reconoce queda como párrafo. No se guarda nada al abrir; el texto
 * pasa a HTML recién cuando la persona guarda.
 */
export function markdownToHtml(source) {
    if (!source?.trim())
        return '';
    if (looksLikeHtml(source))
        return source;
    /**
     * Negrita, cursiva, código y enlaces dentro de una línea.
     *
     * No se escapa acá: la salida pasa entera por el saneador, que escapa lo que
     * queda fuera de una etiqueta. Escapando en los dos lugares, un `<` del texto
     * terminaba como `&amp;lt;` y se leía literal en la pantalla.
     */
    const enLinea = (texto) => texto
        .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label, href) => {
        const seguro = safeHref(href);
        return seguro ? `<a href="${seguro}">${label}</a>` : label;
    })
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
        .replace(/`([^`]+)`/g, '<code>$1</code>');
    const lineas = source.replace(/\r\n/g, '\n').split('\n');
    const salida = [];
    let lista = null;
    const cerrarLista = () => {
        if (lista)
            salida.push(`</${lista}>`);
        lista = null;
    };
    for (const linea of lineas) {
        const limpia = linea.trim();
        if (!limpia) {
            cerrarLista();
            continue;
        }
        const titulo = /^(#{1,4})\s+(.*)$/.exec(limpia);
        if (titulo) {
            cerrarLista();
            // El h1 baja a h2: el título del contenido ya es el h1 de la pantalla, y
            // dos primeros niveles compiten.
            const nivel = Math.max(2, Math.min(4, titulo[1].length + 1));
            salida.push(`<h${nivel}>${enLinea(titulo[2])}</h${nivel}>`);
            continue;
        }
        const punto = /^[-*+]\s+(.*)$/.exec(limpia);
        if (punto) {
            if (lista !== 'ul') {
                cerrarLista();
                salida.push('<ul>');
                lista = 'ul';
            }
            salida.push(`<li>${enLinea(punto[1])}</li>`);
            continue;
        }
        const numerado = /^\d+[.)]\s+(.*)$/.exec(limpia);
        if (numerado) {
            if (lista !== 'ol') {
                cerrarLista();
                salida.push('<ol>');
                lista = 'ol';
            }
            salida.push(`<li>${enLinea(numerado[1])}</li>`);
            continue;
        }
        const cita = /^>\s?(.*)$/.exec(limpia);
        if (cita) {
            cerrarLista();
            salida.push(`<blockquote>${enLinea(cita[1])}</blockquote>`);
            continue;
        }
        cerrarLista();
        salida.push(`<p>${enLinea(limpia)}</p>`);
    }
    cerrarLista();
    return sanitizeRichText(salida.join(''));
}
//# sourceMappingURL=richtext.js.map